import React from 'react';

// Tutorial steps: text comes from the 'tutorial' catalog, example words are per language.

// Visual components for tutorial steps - using tut-letter class to avoid conflicts
const WordExample = ({ word, highlightIndex, highlightType, label }) => (
    <div className="word-example">
        {label && <span className="word-label">{label}</span>}
        <div className="word-letters">
            {word.split('').map((letter, i) => (
                <span
                    key={i}
                    className={`tut-letter ${i === highlightIndex ? `highlight-${highlightType}` : ''}`}
                >
                    {letter}
                </span>
            ))}
        </div>
    </div>
);

const TransformationExample = ({ from, to, changeIndex, type }) => (
    <div className="transformation-example">
        <div className="transform-from">
            <WordExample word={from} />
        </div>
        <span className="transform-arrow">→</span>
        <div className="transform-to">
            <WordExample word={to} highlightIndex={changeIndex} highlightType={type} />
        </div>
    </div>
);

const ButtonExample = ({ color, label }) => (
    <div className="button-example">
        <div className={`example-button ${color}`}>
            <span className="button-icon">→</span>
        </div>
        <span className="button-label">{label}</span>
    </div>
);

const InputExample = ({ word, enterLabel }) => (
    <div className="input-example">
        <div className="example-letter-boxes">
            {word.split('').map((letter, i) => (
                <span key={i} className="tut-letter filled">{letter}</span>
            ))}
        </div>
        <div className="example-submit-row">
            <div className="example-submit-button">
                <span>→</span>
            </div>
            <span className="key">{enterLabel}</span>
        </div>
    </div>
);

// Example words differ by one letter in their own language.
const EXAMPLES = {
    en: {
        ladder: [['COLD'], ['CORD', 2], ['WORD', 0], ['WARD', 1], ['WARM', 3]],
        change: { from: 'CAT', to: 'BAT', index: 0 },
        add: { from: 'DOG', to: 'DOGS', index: 3 },
        remove: { from: 'CART', to: 'CAT' },
        input: 'BAT',
    },
    hr: {
        ladder: [['RUKA'], ['BUKA', 0], ['BURA', 2], ['PURA', 0]],
        change: { from: 'KOD', to: 'KAD', index: 1 },
        add: { from: 'POD', to: 'PLOD', index: 1 },
        remove: { from: 'RODA', to: 'ROD' },
        input: 'KAD',
    },
    de: {
        ladder: [['HEMD'], ['HELD', 2], ['GELD', 0], ['GOLD', 1]],
        change: { from: 'HAUS', to: 'MAUS', index: 0 },
        add: { from: 'RAUM', to: 'TRAUM', index: 0 },
        remove: { from: 'ARZT', to: 'ART' },
        input: 'MAUS',
    },
    es: {
        ladder: [['BESO'], ['PESO', 0], ['PELO', 2], ['PALO', 1]],
        change: { from: 'GATO', to: 'PATO', index: 0 },
        add: { from: 'CARA', to: 'CARTA', index: 3 },
        remove: { from: 'PLATO', to: 'PATO' },
        input: 'PATO',
    },
    fr: {
        ladder: [['FIL'], ['FIN', 2], ['VIN', 0], ['VIE', 2]],
        change: { from: 'BOIS', to: 'MOIS', index: 0 },
        add: { from: 'MOT', to: 'MORT', index: 2 },
        remove: { from: 'MATIN', to: 'MAIN' },
        input: 'MOIS',
    },
    id: {
        ladder: [['BAJU'], ['BAJA', 3], ['RAJA', 0], ['RASA', 2]],
        change: { from: 'BUKU', to: 'SUKU', index: 0 },
        add: { from: 'ALAM', to: 'MALAM', index: 0 },
        remove: { from: 'KOTAK', to: 'KOTA' },
        input: 'SUKU',
    },
    it: {
        ladder: [['SOLE'], ['SALE', 1], ['MALE', 0], ['MARE', 2]],
        change: { from: 'CANE', to: 'PANE', index: 0 },
        add: { from: 'CENA', to: 'SCENA', index: 0 },
        remove: { from: 'PARTE', to: 'ARTE' },
        input: 'PANE',
    },
    pl: {
        ladder: [['NÓŻ'], ['NÓG', 2], ['BÓG', 0], ['BÓL', 2]],
        change: { from: 'KOT', to: 'KĄT', index: 1 },
        add: { from: 'ROK', to: 'KROK', index: 0 },
        remove: { from: 'OKNO', to: 'OKO' },
        input: 'KĄT',
    },
    ru: {
        ladder: [['ТЕСТ'], ['ТОСТ', 1], ['ТОРТ', 2], ['БОРТ', 0]],
        change: { from: 'ДОМ', to: 'ДЫМ', index: 1 },
        add: { from: 'СВЕТ', to: 'СОВЕТ', index: 1 },
        remove: { from: 'БРАК', to: 'РАК' },
        input: 'ДЫМ',
    },
    pt: {
        ladder: [['MAR'], ['MAL', 2], ['SAL', 0], ['SOL', 1]],
        change: { from: 'GATO', to: 'PATO', index: 0 },
        add: { from: 'CAMA', to: 'CALMA', index: 2 },
        remove: { from: 'CARRO', to: 'CARO' },
        input: 'PATO',
    },
    tr: {
        ladder: [['TEN'], ['TAN', 1], ['YAN', 0], ['YAZ', 2]],
        change: { from: 'BAŞ', to: 'TAŞ', index: 0 },
        add: { from: 'KAR', to: 'KART', index: 3 },
        remove: { from: 'KALEM', to: 'KALE' },
        input: 'TAŞ',
    },
};

export function getTutorialSteps(language, t) {
    const ex = EXAMPLES[language] || EXAMPLES.en;
    const last = ex.ladder.length - 1;
    return [
        {
            title: t('tutorial.welcome.title'),
            description: t('tutorial.welcome.description'),
            visual: (
                <div className="goal-visual">
                    {ex.ladder.map(([word, changed], i) => (
                        <React.Fragment key={word}>
                            {i > 0 && <span className="visual-arrow-down">↓</span>}
                            <WordExample
                                word={word}
                                label={i === 0 ? t('tutorial.welcome.start') : i === last ? t('tutorial.welcome.target') : undefined}
                                highlightIndex={changed}
                                highlightType={changed !== undefined ? 'change' : undefined}
                            />
                        </React.Fragment>
                    ))}
                </div>
            ),
        },
        {
            title: t('tutorial.oneLetter.title'),
            description: t('tutorial.oneLetter.description'),
            visual: (
                <div className="changes-visual">
                    <div className="change-type">
                        <span className="change-label">{t('tutorial.oneLetter.change')}</span>
                        <TransformationExample from={ex.change.from} to={ex.change.to} changeIndex={ex.change.index} type="change" />
                    </div>
                    <div className="change-type">
                        <span className="change-label">{t('tutorial.oneLetter.add')}</span>
                        <TransformationExample from={ex.add.from} to={ex.add.to} changeIndex={ex.add.index} type="add" />
                    </div>
                    <div className="change-type">
                        <span className="change-label">{t('tutorial.oneLetter.remove')}</span>
                        <TransformationExample from={ex.remove.from} to={ex.remove.to} changeIndex={-1} type="remove" />
                    </div>
                </div>
            ),
        },
        {
            title: t('tutorial.enter.title'),
            description: t('tutorial.enter.description'),
            visual: <InputExample word={ex.input} enterLabel={t('tutorial.enterKey')} />,
        },
        {
            title: t('tutorial.colors.title'),
            description: t('tutorial.colors.description'),
            visual: (
                <div className="colors-visual">
                    <ButtonExample color="green" label={t('tutorial.colors.valid')} />
                    <ButtonExample color="red" label={t('tutorial.colors.invalid')} />
                    <ButtonExample color="orange" label={t('tutorial.colors.conflict')} />
                </div>
            ),
        },
    ];
}
